import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { fetchIntegration, syncReadwise } from "@/lib/readwise";
import { buildReadwiseReturnTo, getSourceDisplay, type ReadwiseHighlightRow, type ReadwiseLibraryMatch } from "@/lib/readwiseSource";
import type { LucideIcon } from "lucide-react";
import type { Density } from "@/components/readwise/HighlightFilters";

const PAGE_SIZE = 50;
const DENSITY_KEY = "excerptus.readwise.density";
const EXPANDED_KEY = "excerptus.readwise.expanded";

type Args = {
  workspaceId: string | null;
  activePersonaId: string | null;
};

/** Owns Readwise Highlights page state: data load, sync, library matches,
 *  filters/pagination, density + per-row expanded persistence. */
export const useReadwiseHighlights = ({ workspaceId, activePersonaId }: Args) => {
  const [rows, setRows] = useState<ReadwiseHighlightRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [bookFilter, setBookFilter] = useState<string>("__all__");
  const [tagFilter, setTagFilter] = useState<string>("__all__");
  const [sourceFilter, setSourceFilter] = useState<string>("__all__");
  const [page, setPage] = useState(0);
  const [integration, setIntegration] = useState<Awaited<ReturnType<typeof fetchIntegration>>>(null);
  const [libraryMatches, setLibraryMatches] = useState<Record<string, ReadwiseLibraryMatch | null>>({});

  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === "undefined") return "cards";
    const v = localStorage.getItem(DENSITY_KEY);
    return v === "compact" || v === "cards" ? v : "cards";
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = sessionStorage.getItem(EXPANDED_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === "string")) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(Array.from(expanded)));
    } catch {
      /* ignore quota errors */
    }
  }, [expanded]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const load = async () => {
    if (!workspaceId) return;
    setLoading(true);
    const [{ data, error }, integrationRow] = await Promise.all([
      supabase
        .from("readwise_highlights")
        .select(
          "id, external_id, book_title, book_author, book_source, book_cover_url, highlight_text, note, location, location_type, highlighted_at, readwise_url, readwise_tags",
        )
        .eq("workspace_id", workspaceId)
        .order("highlighted_at", { ascending: false, nullsFirst: false })
        .limit(1000),
      activePersonaId ? fetchIntegration(activePersonaId) : Promise.resolve(null),
    ]);
    if (error) toast.error(error.message);
    else setRows(data ?? []);
    setIntegration(integrationRow);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || rows.length === 0) {
      setLibraryMatches({});
      return;
    }

    const titleAuthorPairs = Array.from(new Set(rows.map((row) => `${row.book_title ?? ""}|||${row.book_author ?? ""}`).filter((value) => value !== "|||")));

    Promise.all(titleAuthorPairs.map(async (pair) => {
      const [title, author] = pair.split("|||");
      const query = supabase
        .from("library_items")
        .select("id, title, subtitle, display_title, author_text")
        .eq("workspace_id", workspaceId)
        .ilike("title", title)
        .limit(1);
      const { data } = author
        ? await query.ilike("author_text", author)
        : await query;
      return [pair, ((data?.[0] as { id: string; title: string; subtitle: string | null; display_title: string | null; author_text: string | null } | undefined) ?? null)] as const;
    })).then((entries) => setLibraryMatches(Object.fromEntries(entries)));
  }, [workspaceId, rows]);

  const handleSync = async () => {
    if (!workspaceId || !activePersonaId) return;
    setSyncing(true);
    try {
      const r = await syncReadwise(workspaceId, activePersonaId);
      toast.success(`Synced ${r.imported} highlights`);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const books = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.book_title && set.add(r.book_title));
    return Array.from(set).sort();
  }, [rows]);

  const { favoriteTag, favoriteCount, otherTags } = useMemo(() => {
    const set = new Set<string>();
    let count = 0;
    rows.forEach((r) => {
      r.readwise_tags.forEach((t) => set.add(t));
      if (r.readwise_tags.some((t) => /^favorite$/i.test(t))) count += 1;
    });
    const all = Array.from(set);
    const fav = all.find((t) => /^favorite$/i.test(t)) ?? null;
    const others = all.filter((t) => t !== fav).sort();
    return { favoriteTag: fav, favoriteCount: count, otherTags: others };
  }, [rows]);

  const sources = useMemo(() => {
    const map = new Map<
      string,
      { label: string; Icon: LucideIcon; tokenKey: ReturnType<typeof getSourceDisplay>["tokenKey"] }
    >();
    rows.forEach((r) => {
      const d = getSourceDisplay(r.book_source);
      if (!map.has(d.key)) map.set(d.key, { label: d.label, Icon: d.Icon, tokenKey: d.tokenKey });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (bookFilter !== "__all__" && r.book_title !== bookFilter) return false;
      if (tagFilter !== "__all__" && !r.readwise_tags.includes(tagFilter)) return false;
      if (sourceFilter !== "__all__" && getSourceDisplay(r.book_source).key !== sourceFilter)
        return false;
      if (q) {
        const sourceLabel = getSourceDisplay(r.book_source).label;
        const hay = [
          r.highlight_text,
          r.note ?? "",
          r.book_title ?? "",
          r.book_author ?? "",
          sourceLabel,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, bookFilter, tagFilter, sourceFilter]);

  const readwiseReturnTo = useMemo(
    () => buildReadwiseReturnTo({ q: search, book: bookFilter, tag: tagFilter, source: sourceFilter }),
    [bookFilter, search, sourceFilter, tagFilter],
  );

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const allExpandedOnPage = paged.length > 0 && paged.every((r) => expanded.has(r.id));
  const expandAllOnPage = () =>
    setExpanded((prev) => {
      const next = new Set(prev);
      paged.forEach((r) => next.add(r.id));
      return next;
    });
  const collapseAllOnPage = () =>
    setExpanded((prev) => {
      const next = new Set(prev);
      paged.forEach((r) => next.delete(r.id));
      return next;
    });

  const didMountFiltersRef = useRef(false);
  useEffect(() => {
    if (!didMountFiltersRef.current) {
      didMountFiltersRef.current = true;
      return;
    }
    setPage(0);
    setExpanded(new Set());
  }, [search, bookFilter, tagFilter, sourceFilter]);

  const didMountPageRef = useRef(false);
  useEffect(() => {
    if (!didMountPageRef.current) {
      didMountPageRef.current = true;
      return;
    }
    setExpanded(new Set());
  }, [page]);

  return {
    rows,
    loading,
    syncing,
    search, setSearch,
    bookFilter, setBookFilter,
    tagFilter, setTagFilter,
    sourceFilter, setSourceFilter,
    page, setPage,
    integration,
    libraryMatches,
    density, setDensity,
    expanded,
    toggleExpanded,
    handleSync,
    books,
    favoriteTag, favoriteCount, otherTags,
    sources,
    filtered,
    paged,
    totalPages,
    allExpandedOnPage,
    expandAllOnPage,
    collapseAllOnPage,
    readwiseReturnTo,
  };
};
